import React, { useCallback, useRef, useState } from 'react';

import { Popper } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import { PopperPlacementType } from '@material-ui/core/Popper';
import { Theme } from '@material-ui/core/styles';
import styled from '@material-ui/core/styles/styled';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { ReferenceObject } from 'popper.js';
import { createPortal } from 'react-dom';

import { colorsPalette } from '../../../../theme';
import PickerPare from './components/PickerPare/PickerPare';
import { useStyles } from './styles';
import useClickOutside from './useClickOutside';

function PickerWithRanges({
  placement = 'bottom-start',
  anchorEl = null,
  id,
  value,
  options = [],
  onChange,
  onSubmit,
  isMobile = false,
  wrapperWidth = null,
}: {
  anchorEl?: null | ReferenceObject | (() => ReferenceObject);
  id?: string;
  value?: any | any[];
  placement?: PopperPlacementType;
  options: any[];
  onChange: (opt: any) => void;
  onSubmit: (from: Date, to: Date) => void;
  isMobile?: boolean;
  wrapperWidth?: number | null;
}) {
  const currentDateRef = useRef(new Date());
  const rootRef = useRef(document.querySelector('body'));
  const classes = useStyles(wrapperWidth)();
  const [open, setOpen] = useState(false);
  const [showPickerOnMobile, setShowPickerOnMobile] = useState(false);

  const closeHandler = useCallback(() => {
    setShowPickerOnMobile(false);
    setOpen(false);
  }, []);

  const submitHandler = (startDate, endDate) => {
    if (startDate && endDate) {
      onSubmit(startDate, endDate);
      closeHandler();
    }
  };

  useClickOutside('#pickerWrapper', closeHandler);

  const isCustom = value.value === 'custom';
  const isDesktop = !isMobile;
  const showRanges = isDesktop || !showPickerOnMobile;
  const showPicker = isDesktop ? isCustom : isCustom && showPickerOnMobile;

  return (
    <>
      <div>
        <ButtonBase
          data-testid="openRanges"
          disableRipple
          aria-describedby={id}
          onClick={() => setOpen(true)}
        >
          <SelectHeader>
            <SelectItem>{value?.display}</SelectItem>
            <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
          </SelectHeader>
        </ButtonBase>
      </div>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        className={classes.popper}
        modifiers={{
          flip: {
            enabled: false,
          },
        }}
        transition
      >
        <Wrapper id="pickerWrapper" wrapperWidth={wrapperWidth}>
          {showRanges && (
            <Ranges rounded={!showPicker ? 1 : 0} fullWidth={!showPicker ? 1 : 0}>
              {options.map((opt, index) => {
                const rangeHandler = () => {
                  onChange(opt);
                  const isCustomOpt = opt.value === 'custom';

                  if (isMobile) {
                    setShowPickerOnMobile(opt.value === 'custom');
                  }

                  if (!isCustomOpt) {
                    closeHandler();
                  }
                };

                return (
                  <Range
                    data-testid={opt.value + 'RangeOption'}
                    onClick={rangeHandler}
                    key={index}
                    selected={opt.value === value.value ? 1 : 0}
                  >
                    {opt.display}
                  </Range>
                );
              })}
            </Ranges>
          )}
          {showPicker && (
            <PickerPare
              closeHandler={closeHandler}
              submitHandler={submitHandler}
              isDesktop={isDesktop}
              currentDate={currentDateRef.current}
            />
          )}
        </Wrapper>
      </Popper>
      {rootRef.current && open && createPortal(<Overlay />, rootRef.current)}
    </>
  );
}

export default PickerWithRanges;

const Ranges = styled('ul')(({ rounded, fullWidth }: { rounded: number; fullWidth: number }) => ({
  width: fullWidth ? '100%' : 'initial',
  minWidth: 240,
  margin: 0,
  padding: '24px',
  listStyleType: 'none',
  background: colorsPalette.violetAnalyticsCard,
  borderRadius: rounded ? 20 : '20px 0 0 20px',
}));
const Wrapper = styled('div')(
  ({ wrapperWidth, theme }: { wrapperWidth: null | number; theme: Theme }) => ({
    display: 'flex',
    borderRadius: 20,
    background: colorsPalette.blackSecondary,
    maxWidth: wrapperWidth || 'initial',
    [theme.breakpoints.up('md')]: {
      maxWidth: 600,
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 'initial',
    },
  }),
);
const Range = styled('li')(({ selected }: { selected: number }) => ({
  marginBottom: 5,
  fontSize: 16,
  background: selected && colorsPalette.blue,
  borderRadius: 5,
  padding: 11,
  cursor: 'pointer',
}));
const Overlay = styled('div')({
  position: 'fixed',
  width: '100%',
  height: '100%',
  left: 0,
  top: 0,
  background: 'rgba(27, 25, 37, 0.8)',
  zIndex: 1,
});
const SelectHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
});
const SelectItem = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 'bold',
  color: colorsPalette.gray,
  [theme.breakpoints.up('md')]: {
    fontSize: 16,
  },
}));
